import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isMockPickerPath } from '../utils/quizRoutes';
import { isAutomationBrowser } from '../utils/isAutomationBrowser';

const PHI_SRC = 'https://phi.llc/phi.js';
const PHI_ID = 'phi_5839a8291ec';

/**
 * Injects phi.js after idle so it does not compete with homepage LCP.
 * Attributes match the former index.html snippet.
 */
/** Timed mocks and drills run under /quiz/…; keep the chat bubble out of the way there (not on the /quiz/session/ picker). */
export const hideChatOn = (pathname) => pathname.startsWith('/quiz/') && !isMockPickerPath(pathname);

export default function LoadPhi() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.toggle('phi-chat-off', hideChatOn(pathname));
    return undefined;
  }, [pathname]);

  useEffect(() => {
    if (process.env.REACT_APP_LOCAL_TEST === '1') return undefined;
    if (typeof document === 'undefined') return undefined;
    if (isAutomationBrowser()) return undefined;
    if (document.querySelector('script[data-phi-loader="1"]')) return undefined;

    let idleId;
    let timerId;

    const inject = () => {
      if (isAutomationBrowser()) return;
      if (document.querySelector('script[data-phi-loader="1"]')) return;
      const s = document.createElement('script');
      s.src = PHI_SRC;
      s.defer = true;
      s.dataset.phiLoader = '1';
      s.dataset.id = PHI_ID;
      s.dataset.trackClicks = 'true';
      s.dataset.outboundLinks = 'true';
      s.dataset.scrollDepth = 'true';
      // Phi Digital live chat: messages reach goh's inbox at phi.llc/os/chat.
      s.dataset.chat = 'true';
      s.dataset.chatTheme = 'light';
      s.dataset.chatColor = '0b5cab'; // --accent / --navy (DESIGN.md)
      // Phi's server renders "Chat with us", but reads a custom title on the
      // first client render. Omit it to avoid #418 inside the chat iframe.
      document.body.appendChild(s);
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(inject, { timeout: 3000 });
    } else {
      timerId = window.setTimeout(inject, 1200);
    }

    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, []);

  return null;
}
